/**
 * Configuración de la instancia, resuelta EN RUNTIME.
 *
 * El panel es una SPA estática: si la URL de la API o la marca se leen solo de
 * las variables de Vite, quedan horneadas en el bundle y cada cliente necesita
 * su propia imagen. Para que una única imagen sirva a todas las instancias, el
 * contenedor escribe `/config.js` al arrancar y aquí se lee de ahí.
 *
 * Orden de precedencia:
 *   1. window.__CMS_CONFIG__  → lo inyecta el contenedor desde sus variables.
 *   2. import.meta.env.VITE_* → desarrollo local con .env.
 *   3. valores por defecto neutros.
 */

export interface RuntimeConfig {
  apiUrl?: string;
  frontendUrl?: string;
  brandName?: string;
  brandLogoUrl?: string;
  faviconUrl?: string;
  features?: {
    design?: boolean;
  };
}

declare global {
  interface Window {
    __CMS_CONFIG__?: RuntimeConfig;
  }
}

const runtime: RuntimeConfig = (typeof window !== 'undefined' && window.__CMS_CONFIG__) || {};

function text(runtimeValue: string | undefined, envValue: unknown, fallback: string): string {
  const fromRuntime = typeof runtimeValue === 'string' ? runtimeValue.trim() : '';
  if (fromRuntime !== '') return fromRuntime;

  const fromEnv = typeof envValue === 'string' ? envValue.trim() : '';
  if (fromEnv !== '') return fromEnv;

  return fallback;
}

function flag(runtimeValue: boolean | undefined, envValue: unknown, fallback: boolean): boolean {
  if (typeof runtimeValue === 'boolean') return runtimeValue;

  if (typeof envValue === 'string' && envValue.trim() !== '') {
    return !['false', '0', 'no', 'off'].includes(envValue.trim().toLowerCase());
  }

  return fallback;
}

export const config = {
  apiUrl: text(runtime.apiUrl, import.meta.env.VITE_API_URL, 'http://localhost:9103'),
  frontendUrl: text(runtime.frontendUrl, import.meta.env.VITE_FRONTEND_URL, ''),
  /** Nombre visible del CMS. Vacío en el producto: lo pone cada instancia. */
  brandName: text(runtime.brandName, import.meta.env.VITE_BRAND_NAME, 'CMS'),
  /** Si no hay logo se muestra el nombre como texto. */
  brandLogoUrl: text(runtime.brandLogoUrl, import.meta.env.VITE_BRAND_LOGO_URL, ''),
  features: {
    /**
     * Tema, patrones y plantillas. Solo tiene sentido cuando el frontend consume
     * los tokens del CMS; las webs con diseño propio lo desactivan.
     */
    design: flag(runtime.features?.design, import.meta.env.VITE_FEATURE_DESIGN, true),
  },
} as const;
